<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'

import { type ComboboxOption } from '@/ui/combobox'
import type { CustomerImport, ImportValueMapping } from '@/types/types'

import SelectOptin from './SelectOptin.vue'

const props = defineProps<{
  customerImport: CustomerImport
  choices: Record<'store' | 'optin' | 'has_optin_email', { label: string; value: string }[]> | null
  type: 'store' | 'optin' | 'has_optin_email'
  fileColumn: string
  pongoColumn: string
  isLoadingStoreValues: boolean
  reloadChoices?: () => Promise<void>
}>()

const savedMapping = defineModel<ImportValueMapping[] | null>('savedMapping')

const emit = defineEmits<{
  (e: 'closeModal'): void
  (e: 'saveSettings'): void
}>()

const { t } = useI18n()

const modalMapping = ref<ImportValueMapping[] | null>(null)
const hasUserEditedMapping = ref(false)

const TRUTHY_OPTIN_VALUES = ['1', 'true', 'vrai', 'v', 'oui', 'o', 'yes', 'y', 'si', 's', 'ja', 'j']

const buildValuesFromRawFirst = (
  rawFirstValues: (string | null)[] | undefined,
  resolveMappedValue: (valueInFile: string) => string | null
) => {
  return [...new Set(rawFirstValues ?? [])]
    .filter((value): value is string => value !== null && value !== '')
    .map((valueInFile) => ({
      valueInFile,
      mappedValue: resolveMappedValue(valueInFile)
    }))
}

const mappedColumn = computed(() => {
  return props.customerImport?.columns?.find((column) => column.type === props.type)
})

const storeColumn = computed(() => {
  return props.customerImport?.columns?.find((column) => column.type === 'store')
})

const resolvedColumnValues = computed(() => {
  if (mappedColumn.value?.values?.length) {
    return mappedColumn.value.values
  }

  if (props.type === 'store') {
    return buildValuesFromRawFirst(mappedColumn.value?.rawFirstValues, () => null)
  }

  return buildValuesFromRawFirst(mappedColumn.value?.rawFirstValues, (valueInFile) =>
    TRUTHY_OPTIN_VALUES.includes(valueInFile.trim().toLowerCase()) ? '1' : null
  )
})

const table = computed(() => {
  return {
    columns: {
      valueInFile: {
        label: props.fileColumn,
        headerClass: 'whitespace-nowrap w-[50%]!'
      },
      status: {
        label: '',
        headerClass: 'w-[56px]!'
      },
      mappedValue: {
        label: props.pongoColumn,
        headerClass: 'whitespace-nowrap w-[50%]!'
      }
    },
    rows: modalMapping.value
      ?.find((column) => column.type === props.type)
      ?.values?.map((column, index) => ({
        id: index,
        data: {
          valueInFile: column.valueInFile,
          status: column.mappedValue === 'ignore' ? 'ignore' : 'matched',
          mappedValue: column.mappedValue
        }
      }))
  }
})

const storeOptions = computed<ComboboxOption<string>[]>(() => {
  if (props.type !== 'store' || !storeColumn.value || !props.choices?.[storeColumn.value.type]) {
    return []
  }
  return [
    { value: 'ignore', label: t('pages.import.importPage.mapping.ignoreStore') },
    ...props.choices[storeColumn.value.type].map((choice) => ({
      value: String(choice.value),
      label: choice.label
    }))
  ]
})

const isMappingDataLoaded = computed(() => {
  if (!resolvedColumnValues.value.length) {
    return false
  }

  if (props.type === 'store') {
    if (props.isLoadingStoreValues) {
      return false
    }

    return Boolean(props.choices?.store?.length)
  }

  return true
})

const hasLoadFailed = computed(() => {
  if (props.isLoadingStoreValues || isMappingDataLoaded.value) {
    return false
  }

  return props.type === 'store' || props.type === 'optin' || props.type === 'has_optin_email'
})

const handleChangeMappedValue = ({ newMappedValue, valueInFile }: { newMappedValue: string; valueInFile: string }) => {
  hasUserEditedMapping.value = true

  const columnToUpdate = props.customerImport?.columns?.find((column) => column.type === props.type)
  const updatedMapping = modalMapping.value?.find((setting) => setting.type === columnToUpdate.type)

  if (updatedMapping) {
    updatedMapping.values = updatedMapping.values.map((val) =>
      val.valueInFile === valueInFile ? { ...val, mappedValue: newMappedValue } : val
    )
  } else {
    modalMapping.value?.push({ type: columnToUpdate.type, values: [{ valueInFile, mappedValue: newMappedValue }] })
  }
}

const saveMapping = () => {
  savedMapping.value = [
    ...(savedMapping.value?.filter((mapping) => mapping.type !== props.type) || []),
    modalMapping.value?.find((mapping) => mapping.type === props.type)
  ]
  emit('saveSettings')
  emit('closeModal')
}

const createModalMapping = () => {
  if (hasUserEditedMapping.value) {
    return
  }

  if (savedMapping.value?.find((mapping) => mapping.type === props.type)) {
    modalMapping.value = [...savedMapping.value]
    return
  }

  if (isMappingDataLoaded.value) {
    modalMapping.value = props.customerImport.columns.map((column) => ({
      type: column.type,
      values:
        (column.type === props.type ? resolvedColumnValues.value : column.values)?.map((value) => ({
          valueInFile: value.valueInFile,
          mappedValue: value.mappedValue ?? 'null'
        })) ?? []
    }))
  }
}

watch(
  () => [
    isMappingDataLoaded.value,
    mappedColumn.value?.values,
    props.choices?.store,
    props.choices?.optin,
    props.choices?.has_optin_email,
    props.isLoadingStoreValues
  ],
  () => {
    createModalMapping()
  },
  { deep: true }
)

onMounted(() => {
  createModalMapping()
})

const handleOpenChange = (open: boolean) => {
  if (!open) emit('closeModal')
}
</script>

<template>
  <Dialog :open="true" @update:open="handleOpenChange">
    <DialogContent class="flex max-h-[90vh] flex-col sm:max-w-5xl">
      <DialogHeader class="-mx-6 -mt-6 border-b border-border bg-white px-6 py-4">
        <DialogTitle>{{ t('pages.import.importPage.mapping.modalTitles.' + type) }}</DialogTitle>
      </DialogHeader>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <div class="flex flex-col">
          <div
            v-if="!isMappingDataLoaded"
            class="flex flex-col items-center gap-4 rounded-lg border bg-muted/30 p-8 text-center"
          >
            <template v-if="type === 'store' && isLoadingStoreValues">
              <Loader2 class="h-8 w-8 animate-spin text-primary" />
              <p class="text-sm text-muted-foreground">
                {{ t('pages.import.importPage.mapping.loadingStoreValues') }}
              </p>
            </template>
            <div v-else-if="hasLoadFailed" class="flex flex-col items-center gap-4">
              <p class="text-sm text-muted-foreground">
                {{
                  type === 'store'
                    ? t('pages.import.importPage.mapping.loadStoreValuesFailed')
                    : t('pages.import.importPage.mapping.loadMappingValuesEmpty')
                }}
              </p>
              <Button v-if="reloadChoices && type === 'store'" variant="outline" @click="reloadChoices()">
                {{ t('global.words.refresh') }}
              </Button>
            </div>
            <AppSkeleton v-else class="w-full overflow-hidden rounded-lg border" />
          </div>
          <AppTable v-else :columns="table.columns" :rows="table.rows">
            <template #td-valueInFile="{ data }">
              <div class="flex flex-col text-left">
                <div
                  class="inline-block whitespace-normal rounded-lg border bg-muted px-4 py-2 text-sm font-medium text-foreground"
                >
                  {{ data.valueInFile || t('pages.import.importPage.mapping.emptyValue') }}
                </div>
              </div>
            </template>

            <template #td-status="{ data }">
              <BaseIcon
                v-if="data.status === 'matched'"
                name="HeroArrowRightIconOutline"
                class="h-6 w-6 text-green-500"
              />
              <XCircleIcon v-if="data.status === 'ignore'" class="h-6 w-6 text-red-500" />
            </template>

            <template #td-mappedValue="{ data }">
              <div class="flex w-full flex-col justify-center gap-2">
                <ComboboxSelect
                  v-if="type === 'store' && storeColumn && choices && choices[storeColumn.type]"
                  v-model="data.mappedValue"
                  :options="storeOptions"
                  :placeholder="t('pages.import.importPage.mapping.unkownValue')"
                  :empty-message="t('global.noResult')"
                  :search-threshold="0"
                  @update:model-value="
                    (v) => handleChangeMappedValue({ newMappedValue: v, valueInFile: data.valueInFile })
                  "
                />

                <SelectOptin
                  v-if="type === 'optin' || type === 'has_optin_email'"
                  v-model="data.mappedValue"
                  :type="type"
                  @change="
                    (event) =>
                      handleChangeMappedValue({
                        newMappedValue: event.value,
                        valueInFile: data.valueInFile
                      })
                  "
                />
              </div>
            </template>
          </AppTable>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="emit('closeModal')">
          {{ t('global.words.cancel') }}
        </Button>
        <Button :disabled="!isMappingDataLoaded || isLoadingStoreValues" @click="saveMapping">
          {{ t('global.words.confirm') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
